import { PlusOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Inventory, InventoryTransfer, UserBasic } from '@hefe/models';
import { currentUserIsAdminSelector } from '@hefe/store';
import { formatDate, formatDateTime, fullUserName } from '@hefe/utils';
import { Button } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomTable from '../../../core/components/table/custom-table.component';
import CreateInventoryTransferForm from '../../forms/create-inventory-transfer/create-inventory-transfer.form';
import InventoryTransferActionsComponent from '../inventory-transfer-actions/inventory-transfer-actions.component';

interface InventoryTransfersComponentProps {
  inventory: Inventory;
  loading?: boolean;
}

export default function InventoryTransfersComponent(
  props: InventoryTransfersComponentProps
) {
  const { inventory, loading } = props;

  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const [newTransferModalOpen, setNewTransferModalOpen] = useState(false);

  const onClickNewTransfer = () => {
    setNewTransferModalOpen(true);
  };

  const hasActiveTransfer = useMemo(() => {
    return inventory?.transfers?.some((transfer) => !transfer.transferred_at);
  }, [inventory]);

  return (
    <>
      <CustomTable
        rowKey="id"
        name="inventory-transfers"
        hideColumnSelector
        loading={loading}
        columns={[
          {
            key: 'type',
            dataIndex: 'type',
            title: t('inventory.transfers.attributes.type'),
            default: true,
            render: (type: string) =>
              t(`inventory.transfers.types.${type.toLowerCase()}`),
          },
          {
            key: 'from_user',
            dataIndex: 'from_user',
            title: t('inventory.transfers.attributes.from_user'),
            default: true,
            render: (from_user: UserBasic) =>
              from_user ? fullUserName(from_user) : t('inventory.pool.title'),
          },
          {
            key: 'to_user',
            dataIndex: 'to_user',
            title: t('inventory.transfers.attributes.to_user'),
            default: true,
            render: (from_user: UserBasic, transfer: InventoryTransfer) =>
              from_user
                ? fullUserName(from_user)
                : transfer.share_transfer_id
                ? t('pending')
                : t('inventory.pool.title'),
          },
          {
            key: 'created_at',
            dataIndex: 'created_at',
            title: t('inventory.transfers.attributes.requested_at'),
            default: true,
            render: (created_at: string) => formatDateTime(created_at),
          },
          {
            key: 'transferred_at',
            dataIndex: 'transferred_at',
            title: t('inventory.transfers.attributes.transferred_at'),
            default: true,
            render: (transferred_at: string) =>
              transferred_at ? formatDateTime(transferred_at) : t('pending'),
          },
          {
            key: 'due_date',
            dataIndex: 'due_date',
            title: t('inventory.transfers.attributes.due_date'),
            default: true,
            render: (due_date: string) =>
              due_date ? formatDate(due_date) : '---',
          },
          {
            key: 'actions',
            title: '...',
            default: true,
            render: (transfer: InventoryTransfer) => (
              <Button.Group>
                <InventoryTransferActionsComponent
                  inventory={inventory}
                  transfer={transfer}
                />
              </Button.Group>
            ),
          },
        ]}
        dataSource={inventory?.transfers ?? []}
        toolbarItems={[
          !inventory.possessor_id && !hasActiveTransfer && userIsAdmin && (
            <Button
              key="new-transfer-button"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => onClickNewTransfer()}
            >
              {t('inventory.transfers.actions.new')}
            </Button>
          ),
        ]}
      />
      <CreateInventoryTransferForm
        inventory={inventory}
        open={newTransferModalOpen}
        setOpen={(open) => setNewTransferModalOpen(open)}
      />
    </>
  );
}
